import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CheckCircle2, Cloud, GitBranch, TrendingUp } from 'lucide-react';
import type { Domain } from '@lizrd/contracts';
import { Modal } from '../../components/ui/modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert } from '../../components/ui/alert';
import { Confetti } from '../../components/ui/confetti';
import { StepProgress } from '../../components/ui/step-progress';
import type { StepState } from '../../components/ui/stepper';
import { ConnectPanel } from '../../components/connect/ConnectPanel';
import { SyncPanel } from '../../components/sync/SyncPanel';
import { createDomain, getRecommendations, listIntegrations, updateDomain } from '../../lib/api';
import { usd } from '../../lib/graph-visuals';

type Step = 0 | 1 | 2 | 3; // name · connect · sync · ready
const STEP_LABELS = ['Name', 'Connect', 'Sync', 'Ready'] as const;

/** What a domain ties together — the same reference shown in onboarding's name step. */
const CONTAINS = [
  { icon: Cloud, text: 'Cloud accounts & projects — AWS, GCP, Azure' },
  { icon: GitBranch, text: 'The code repositories that deploy into them' },
  { icon: TrendingUp, text: 'Its spend, waste, and realized savings over time' },
];

/**
 * The guided "add a domain" flow as a modal — the onboarding sequence (Name →
 * Connect → Sync → Ready) for a subsequent domain, reusing the shared ConnectPanel
 * and SyncPanel. Unlike mandatory onboarding it's dismissible at any step (the
 * domain persists once named) and the sync is non-blocking ("Finish in the
 * background"). Pass `resumeDomain` to re-enter the flow at Connect for a domain
 * that was created but never set up.
 */
export function AddDomainModal({
  open,
  onClose,
  resumeDomain,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  resumeDomain?: Domain | null;
  onChanged?: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [step, setStep] = useState<Step>(0);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState<Domain | null>(null);
  const [connectReady, setConnectReady] = useState(false);
  const [settled, setSettled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on each open; when resuming an existing domain, jump straight to Connect.
  useEffect(() => {
    if (!open) return;
    setConnectReady(false);
    setSettled(false);
    setError(null);
    if (resumeDomain) {
      setDomain(resumeDomain);
      setName(resumeDomain.name);
      setStep(1);
    } else {
      setDomain(null);
      setName('');
      setStep(0);
    }
  }, [open, resumeDomain]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['domains'] });
    qc.invalidateQueries({ queryKey: ['subscription'] });
    qc.invalidateQueries({ queryKey: ['domain-assessments'] });
    onChanged?.();
  };

  // Create (or rename on back/forward) — idempotent so stepping back never spawns a
  // duplicate domain.
  const save = useMutation({
    mutationFn: () =>
      domain ? updateDomain(domain.id, { name: name.trim() }) : createDomain(name.trim()),
    onSuccess: (d) => {
      setDomain(d);
      refresh();
      setStep(1);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save domain'),
  });

  function goToDomain() {
    if (domain) {
      refresh();
      navigate(`/domains/${domain.id}/overview`);
    }
    onClose();
  }

  const progress = STEP_LABELS.map((label, i) => ({
    label,
    state: (i < step ? 'done' : i === step ? 'active' : 'pending') as StepState,
  }));

  const title = (
    <div className="space-y-2">
      {/* The "Add a domain" case is already labelled by the step's green eyebrow
          below, so only the resume flow needs a title here. */}
      {resumeDomain && (
        <p className="font-display text-sm font-bold text-bone">Finish domain setup</p>
      )}
      <StepProgress steps={progress} spinActive={false} />
    </div>
  );

  const footer = (() => {
    if (step === 0) {
      return (
        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Create domain'}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      );
    }
    if (step === 1) {
      return (
        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="ghost" onClick={() => setStep(0)}>
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back
          </Button>
          <Button type="button" disabled={!connectReady} onClick={() => setStep(2)}>
            {connectReady ? 'Continue' : 'Connect a cloud to continue'}
            {connectReady && <ArrowRight className="h-4 w-4" aria-hidden />}
          </Button>
        </div>
      );
    }
    if (step === 2) {
      return (
        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="ghost" onClick={goToDomain}>
            Finish in the background
          </Button>
          <Button
            type="button"
            disabled={!settled}
            title={settled ? undefined : 'Available once the first sync finishes'}
            onClick={() => setStep(3)}
          >
            {settled ? 'Continue' : 'Syncing…'}
            {settled && <ArrowRight className="h-4 w-4" aria-hidden />}
          </Button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setDomain(null);
            setName('');
            setConnectReady(false);
            setSettled(false);
            setStep(0);
          }}
        >
          Add another
        </Button>
        <Button type="button" onClick={goToDomain}>
          Go to {domain?.name ?? 'domain'}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    );
  })();

  return (
    <Modal open={open} onClose={onClose} title={title} footer={footer} maxWidth="2xl">
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {step === 0 && (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-iris">
              Add a domain
            </p>
            <h2 className="mt-1.5 font-display text-xl font-bold text-bone">Name this domain</h2>
            <p className="mt-1 text-sm text-muted">
              A domain is a slice of your infrastructure you manage as one — give this one a name
              your team already uses.
            </p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && !save.isPending) save.mutate();
            }}
            className="space-y-1.5"
          >
            <label htmlFor="add-domain-name" className="block text-sm font-semibold text-bone">
              Domain name
            </label>
            <Input
              id="add-domain-name"
              autoFocus
              placeholder="e.g. Growth, Checkout, ML Platform"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-xs text-muted">
              A product, a service, or one team’s architecture. You can rename it later.
            </p>
          </form>
          <div className="rounded-md border border-hairline bg-surface-2 p-4">
            <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
              A domain groups
            </p>
            <ul className="mt-3 space-y-2.5">
              {CONTAINS.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-2.5 text-sm text-haze">
                  <Icon className="h-4 w-4 flex-none text-iris" aria-hidden />
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {step === 1 && domain && (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-iris">Connect</p>
            <h2 className="mt-1.5 font-display text-xl font-bold text-bone">
              Connect {domain.name}’s cloud
            </h2>
            <p className="mt-1 text-sm text-muted">
              Attach the cloud accounts (and optionally code) this domain covers — read-only, about
              a minute each.
            </p>
          </div>
          <ConnectPanel envId={domain.id} onReadyChange={setConnectReady} />
        </div>
      )}

      {step === 2 && domain && (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-iris">First sync</p>
            <h2 className="mt-1.5 font-display text-xl font-bold text-bone">
              Mapping {domain.name}
            </h2>
            <p className="mt-1 text-sm text-muted">
              Lizrd is reading this domain’s resources and building its graph. Safe to leave — it
              keeps running in the background.
            </p>
          </div>
          <SyncPanel envId={domain.id} onSettled={() => setSettled(true)} />
        </div>
      )}

      {step === 3 && domain && <ReadyStep domainId={domain.id} name={domain.name} />}
    </Modal>
  );
}

/** The per-domain "ready" summary — this domain's own found opportunities and
 * estimated monthly savings, with confetti only when there's a real result. */
function ReadyStep({ domainId, name }: { domainId: string; name: string }) {
  const recs = useQuery({
    queryKey: ['recommendations', domainId],
    queryFn: () => getRecommendations(domainId),
  });
  const ints = useQuery({
    queryKey: ['integrations', domainId],
    queryFn: () => listIntegrations(domainId),
  });

  const { opportunities, estimated } = useMemo(() => {
    const openItems = (recs.data?.items ?? []).filter((r) => r.status === 'open');
    return {
      opportunities: openItems.length,
      estimated: openItems.reduce((s, r) => s + r.estimatedMonthlySaving, 0),
    };
  }, [recs.data]);
  const cloudCount = (ints.data ?? []).filter(
    (i) => i.kind === 'CLOUD' && i.status === 'CONNECTED',
  ).length;
  const hasValue = estimated > 0;

  return (
    <div className="relative text-center">
      <Confetti active={opportunities > 0} onDone={() => {}} />
      <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success-dim">
        <CheckCircle2 className="h-7 w-7 text-success" aria-hidden />
      </span>
      <h2 className="mt-3 font-display text-xl font-bold text-bone">{name} is ready</h2>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted">
        Mapped and {hasValue ? 'already finding savings' : 'ready to optimize'} — here’s what’s
        waiting on its dashboard.
      </p>

      {hasValue && (
        <div className="mt-4">
          <div className="font-display text-4xl font-bold leading-none tracking-tight text-iris tnum">
            {usd(estimated)}
          </div>
          <p className="mt-1.5 text-sm text-haze">in potential monthly savings on this domain</p>
        </div>
      )}

      <div className="mt-5 flex divide-x divide-hairline rounded-md border border-hairline">
        <div className="flex-1 p-3">
          <div className="font-display text-xl font-bold tnum text-bone">{opportunities}</div>
          <div className="mt-1 text-xs text-muted">opportunities</div>
        </div>
        <div className="flex-1 p-3">
          <div className="font-display text-xl font-bold tnum text-bone">{cloudCount}</div>
          <div className="mt-1 text-xs text-muted">
            {cloudCount === 1 ? 'cloud connected' : 'clouds connected'}
          </div>
        </div>
      </div>
    </div>
  );
}
